// src/pages/Login.js
import { useState, useCallback } from 'react';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Shield } from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

const Login = ({ onLogin, lang, onLangChange }) => {
  const { t } = useTranslation(lang);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const login_url = process.env.REACT_APP_LOGIN_URL;
  const navigate = useNavigate();


  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!email) {
      newErrors.email = t('emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('invalidEmail');
    }

    if (!password) {
      newErrors.password = t('passwordRequired');
    } else if (password.length < 3) {    // modifier en fonction des normes de sécurité
      newErrors.password = t('passwordTooShort');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [email, password, t]);


const handleSubmit = useCallback(async (e) => {
  e.preventDefault();
  setLoginError('');

  if (!validateForm()) return;

  setIsLoading(true);

  try {
    const { data } = await api.post(login_url, {
      email,
      password,
    });

    if (!data?.result?.access_token) {
      setLoginError(data?.request.message || t('loginError'));
      return;
    }

    const userData = {
      email,
      rememberMe,
      token: data.result?.access_token,
      name: data.result?.user?.name || 'Utilisateur',
      role: data.result?.user?.role || 'user',
    };

    onLogin(userData);

  } catch (error) {
    if (error.response?.status === 401) {
      setLoginError(t('loginInvalid') || 'Identifiants incorrects');
    } else {
      console.error('Erreur de connexion:', error);
      setLoginError(t('networkError') || 'Erreur réseau');
    }
  } finally {
    setIsLoading(false);
  }
}, [email, password, rememberMe, validateForm, onLogin, t]);


  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: 'linear-gradient(135deg, #000000 0%, #764ba2 100%)'
    }}>
      {/* Partie gauche - Branding */}
      <div style={{
        flex: 1,
        display: window.innerWidth < 768 ? 'none' : 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
        color: 'white'
      }}>
        <div style={{
          width: '100px',
          height: '100px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '32px',
          backdropFilter: 'blur(10px)'
        }}>
          <Shield size={50} />
        </div>
        <h1 style={{
          fontSize: '42px',
          fontWeight: 'bold',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          Admin Panel
        </h1>
        <p style={{
          fontSize: '18px',
          opacity: 0.9,
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          {t('overview') || 'Gérez votre entreprise en toute simplicité'}
        </p>
      </div>

      {/* Partie droite - Formulaire */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '450px',
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          {/* Sélecteur de langue */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '24px'
          }}>
            <select
              value={lang}
              onChange={(e) => onLangChange(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#666'
              }}
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>

          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '8px'
            }}>
              {t('login')}
            </h2>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              {t('overview') || 'Connectez-vous à votre compte'}
            </p>
          </div>

          {/* Error message */}
          {loginError && (
            <div style={{
              padding: '12px 16px',
              background: '#fee',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              <AlertCircle size={18} />
              {loginError}
            </div>
          )}

          {/* Formulaire */}
          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {t('email')}
              </label>
              <div style={{ position: 'relative' }}>
                <Mail
                  size={20}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af'
                  }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors({ ...errors, email: '' });
                    setLoginError('');
                  }}
                  placeholder={t('emailPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 44px',
                    border: `1px solid ${errors.email ? '#ef4444' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                  onBlur={(e) => e.target.style.borderColor = errors.email ? '#ef4444' : '#e5e7eb'}
                />
              </div>
              {errors.email && (
                <p style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {t('password')}
              </label>
              <div style={{ position: 'relative' }}>
                <Lock
                  size={20}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af'
                  }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors({ ...errors, password: '' });
                    setLoginError('');
                  }}
                  placeholder={t('passwordPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '12px 44px 12px 44px',
                    border: `1px solid ${errors.password ? '#ef4444' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                  onBlur={(e) => e.target.style.borderColor = errors.password ? '#ef4444' : '#e5e7eb'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember me & Forgot password */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151'
              }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer'
                  }}
                />
                {t('rememberMe')}
              </label>
              <button
                type="button"
                onClick={() => navigate('forgot-password')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#7c3aed',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {t('forgotPasswordLink')}
              </button>

                            <button
                type="button"
                onClick={() => navigate('update-password')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#7c3aed',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {t('updatePassword')}
              </button>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '14px',
                background: isLoading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'transform 0.2s',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
              }}
              onMouseEnter={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => !isLoading && (e.target.style.transform = 'translateY(0)')}
            >
              {isLoading ? '...' : t('loginButton')}
            </button>
          </form>

 
        </div>
      </div>
    </div>
  );
};

export default Login;